import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Logger } from 'projects/common/src/app/classes';
import { IMachineContext } from 'projects/common/src/app/interfaces/machine-context';
import { CacheService, MiningToastService } from 'projects/common/src/app/services';
import { ExcelExportService } from 'projects/common/src/app/services/utils/excel-export.service';
import { IFilterConfig } from 'src/app/modules/core/interfaces/filter-config';
import { GoogleAnalyticsService } from 'src/app/modules/core/services/google/google-analytics.service';
import { LanguageService } from '../../../../../../projects/common/src/app/services/parameters/language/language.service';
import { excelExportDateFormat } from '../../../../../../projects/common/src/app/services/utils/util.service';
import { ICheckFilter } from '../../../core/interfaces/check-filter';
import { IOutputFilter } from '../../../core/interfaces/output-filter';
import { FilterHandlerService } from '../../../core/services/filter/filter-handler.service';
import { ILayoutConfig } from '../../../layout/interfaces/layout-config';
import { LayoutConfigService } from '../../../layout/services/layout-config.service';
import { ILoopByOperatorResponse } from '../../interfaces/report-loop-by-operator';
import { ReportLoopByOperatorService } from '../../services/report-loop-by-operator.service';

@Component({
  selector: 'app-loop-by-operator-shell',
  templateUrl: './loop-by-operator-shell.component.html',
  styleUrls: ['./loop-by-operator-shell.component.scss'],
})
export class LoopByOperatorShellComponent extends Logger implements OnInit, OnDestroy {
  public showFilter: ICheckFilter;
  public selectedFilters: IOutputFilter;
  public message: string = null;
  public reportResult: ILoopByOperatorResponse;
  public canDownload = false;
  public hasData = false;
  public isLhd: boolean;
  public isTruck: boolean;
  public systemError: string;
  public filterConfig: IFilterConfig;
  public btnSpinner: boolean;
  public disabledExcelBtn: boolean;

  private machineType: IMachineContext;

  constructor(
    protected layoutConfigService: LayoutConfigService,
    protected languageService: LanguageService,
    protected loopOperatorService: ReportLoopByOperatorService,
    protected filterHandlerService: FilterHandlerService,
    protected cacheService: CacheService,
    protected googleAnalyticsService: GoogleAnalyticsService,
    protected _excelExportService: ExcelExportService,
    protected _toastService: MiningToastService,
    protected cdr: ChangeDetectorRef,
  ) {
    super();
    this.disabledExcelBtn = false;
  }

  /**
   * On init component
   */
  public ngOnInit(): void {
    this.isLhd = true;
    this.isTruck = false;
    this.initLayoutTitle();
    this.declareFilters();
    this.cdr.detectChanges();
  }

  /**
   * Function to declare filter to show
   */
  protected declareFilters(): void {
    const allMachines = this.cacheService.session('machineCollection') as IMachineContext[];
    this.machineType = allMachines ? allMachines.find((v) => (this.isTruck ? v.code === 'TRU' : v.code === 'SCOP')) : null;

    this.filterConfig = {
      dates: { required: true },
      shift: { required: true },
      machineType: { required: true, uniqueSelection: this.machineType },
      mine: { required: true },
      operator: { required: true, multiselect: true },
      cleanButton: true,
    };
  }

  /**
   * Function to select icon image, title & subtitle
   */
  protected initLayoutTitle(): void {
    const subtitle = this.isLhd ? 'report-loops-by-operator' : 'report-loops-by-operator-truck';
    const config: ILayoutConfig = {
      title: 'reports',
      subtitle,
      imageKey: 'report',
      imageType: 'section',
    };
    this.layoutConfigService.setConfig(config);
  }

  /**
   * Function to get report data from received filters
   *
   * @param filters Filters selected to get data
   */
  public getReportByOperatorData(selectedFilters: IOutputFilter): void {
    this.filterConfig.isLoading = true;
    if (selectedFilters) {
      this.selectedFilters = selectedFilters;
      this.loopOperatorService.getLoopByOperatorFilterResult(this.selectedFilters, this.isTruck).then(
        (response) => {
          this.reportResult = response;
          const thereIsLoopOperatorResult = !!this.reportResult.loopsDetailByDate && this.reportResult.loopsDetailByDate.length > 0;
          const thereIsLoopByMachineTable =
            !!this.reportResult?.totalLoopsByOperator && this.reportResult?.totalLoopsByOperator.length > 0
              ? this.reportResult.totalLoopsByOperator
              : !!this.reportResult?.totalLoopsByOperator && this.reportResult?.totalLoopsByOperator.length > 0;
          const thereIsLoopByShiftCards = !!this.reportResult.totalLoopsByShift && this.reportResult.totalLoopsByShift.length > 0;

          if (thereIsLoopOperatorResult && thereIsLoopByMachineTable && thereIsLoopByShiftCards) {
            this.canDownload = true;
            this.hasData = true;
            this.message = null;
          } else {
            this.hasData = false;
            this.canDownload = false;
            this.message = this.languageService.message('m034');
          }
          this.systemError = '';
          this.filterConfig.isLoading = false;
        },
        ({ name, message }) => {
          this.error(message);
          this.systemError = name === 'TimeoutError' ? 'm014' : 'm017';
          this.hasData = false;
          this.canDownload = false;
          this.filterConfig.isLoading = false;
        },
      );
    }
    const filters = {
      reports: 'loop-by-operator',
    };
    this.googleAnalyticsService.sendCustomEvent('filter', filters);
  }

  /**
   * Function to download report in excel format
   */
  public downloadExcel(): void {
    const selectRangeDate = {
      startDate: excelExportDateFormat(this.selectedFilters.startDate),
      endDate: excelExportDateFormat(this.selectedFilters.endDate),
    };

    const nameFile = this.machineType.code === 'TRU' ? 'loop-operator-report-excel-filename' : 'shelves-operator-report-excel-filename';
    this.btnSpinner = true;
    this.disabledExcelBtn = true;

    this.loopOperatorService.GetLoopByOperatorReportExcel(this.selectedFilters, this.isTruck).subscribe({
      next: (response) => {
        this._excelExportService.exportAsExcel(response, nameFile, selectRangeDate, null, false);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastSuccess();
      },
      error: (error) => {
        console.error(error);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastError();
      },
    });
  }

  ngOnDestroy(): void {
    this.layoutConfigService.setConfig(null);
  }
}
